import { ref, onBeforeUnmount, watch } from 'vue'
import type { Ref } from 'vue'

/**
 * Hand-rolled drag + resize primitive for floating panels (detached chat
 * windows, call windows). Avoids pulling in @vueuse/core just for one
 * utility — ~130 lines of pointer-event handling, no new deps.
 *
 * Usage:
 *   const frame = useDragResize({
 *     headerEl, gripEl,
 *     initial: { x: 100, y: 100, width: 360, height: 480 },
 *     min: { width: 280, height: 320 }
 *   })
 *   // frame.x, frame.y, frame.width, frame.height are reactive refs
 *   // Listen for frame.onCommit(rect) to persist to localStorage
 *
 * Constraints: keeps the frame inside the viewport (at least 40 px of
 * header visible) and enforces min/max size. Pointer capture means the
 * drag/resize survives the cursor leaving the window during the gesture.
 */

export interface Rect {
    x: number
    y: number
    width: number
    height: number
}

export interface UseDragResizeOptions {
    headerEl: Ref<HTMLElement | null>
    gripEl: Ref<HTMLElement | null>
    initial: Rect
    min?: { width: number; height: number }
    max?: { width: number; height: number }
    /** Fired once at the end of drag or resize — use to persist the rect. */
    onCommit?: (rect: Rect) => void
}

const DEFAULT_MIN = { width: 280, height: 320 }

export function useDragResize(opts: UseDragResizeOptions) {
    const x = ref(opts.initial.x)
    const y = ref(opts.initial.y)
    const width = ref(opts.initial.width)
    const height = ref(opts.initial.height)

    const min = opts.min || DEFAULT_MIN
    const max = opts.max

    let dragging = false
    let resizing = false
    let startX = 0, startY = 0
    let startRect: Rect = { x: 0, y: 0, width: 0, height: 0 }
    let pointerId: number | null = null

    function clamp() {
        if (typeof window === 'undefined') return
        const vw = window.innerWidth
        const vh = window.innerHeight

        if (max) {
            width.value = Math.min(width.value, max.width)
            height.value = Math.min(height.value, max.height)
        }
        width.value = Math.max(min.width, width.value)
        height.value = Math.max(min.height, height.value)
        width.value = Math.min(width.value, vw)
        height.value = Math.min(height.value, vh)

        // Keep at least 40 px of header visible horizontally, and always on-screen vertically
        x.value = Math.min(x.value, vw - 40)
        x.value = Math.max(x.value, -width.value + 40)
        y.value = Math.min(y.value, vh - 40)
        y.value = Math.max(y.value, 0)
    }

    function currentRect(): Rect {
        return { x: x.value, y: y.value, width: width.value, height: height.value }
    }

    /* ───── Drag ───── */

    function onDragStart(e: PointerEvent) {
        // Only primary button; let inputs/buttons inside the header still work
        if (e.button !== 0) return
        const target = e.target as HTMLElement
        if (target.closest('button, input, textarea, select, a, [data-no-drag]')) return
        dragging = true
        pointerId = e.pointerId
        startX = e.clientX
        startY = e.clientY
        startRect = currentRect()
        opts.headerEl.value?.setPointerCapture(e.pointerId)
        e.preventDefault()
    }

    function onDragMove(e: PointerEvent) {
        if (!dragging || e.pointerId !== pointerId) return
        x.value = startRect.x + (e.clientX - startX)
        y.value = startRect.y + (e.clientY - startY)
        clamp()
    }

    function onDragEnd(e: PointerEvent) {
        if (!dragging || e.pointerId !== pointerId) return
        dragging = false
        pointerId = null
        opts.headerEl.value?.releasePointerCapture(e.pointerId)
        opts.onCommit?.(currentRect())
    }

    /* ───── Resize ───── */

    function onResizeStart(e: PointerEvent) {
        if (e.button !== 0) return
        resizing = true
        pointerId = e.pointerId
        startX = e.clientX
        startY = e.clientY
        startRect = currentRect()
        opts.gripEl.value?.setPointerCapture(e.pointerId)
        e.preventDefault()
        e.stopPropagation()
    }

    function onResizeMove(e: PointerEvent) {
        if (!resizing || e.pointerId !== pointerId) return
        width.value = startRect.width + (e.clientX - startX)
        height.value = startRect.height + (e.clientY - startY)
        clamp()
    }

    function onResizeEnd(e: PointerEvent) {
        if (!resizing || e.pointerId !== pointerId) return
        resizing = false
        pointerId = null
        opts.gripEl.value?.releasePointerCapture(e.pointerId)
        opts.onCommit?.(currentRect())
    }

    /* ───── Wiring ───── */

    // Watch for the element refs to resolve (components mount async) and then
    // attach listeners. Re-attach if refs change.
    const stopHeaderWatch = watch(() => opts.headerEl.value, (el, oldEl) => {
        if (oldEl) {
            oldEl.removeEventListener('pointerdown', onDragStart)
            oldEl.removeEventListener('pointermove', onDragMove)
            oldEl.removeEventListener('pointerup', onDragEnd)
            oldEl.removeEventListener('pointercancel', onDragEnd)
        }
        if (el) {
            el.addEventListener('pointerdown', onDragStart)
            el.addEventListener('pointermove', onDragMove)
            el.addEventListener('pointerup', onDragEnd)
            el.addEventListener('pointercancel', onDragEnd)
        }
    }, { immediate: true, flush: 'post' })

    const stopGripWatch = watch(() => opts.gripEl.value, (el, oldEl) => {
        if (oldEl) {
            oldEl.removeEventListener('pointerdown', onResizeStart)
            oldEl.removeEventListener('pointermove', onResizeMove)
            oldEl.removeEventListener('pointerup', onResizeEnd)
            oldEl.removeEventListener('pointercancel', onResizeEnd)
        }
        if (el) {
            el.addEventListener('pointerdown', onResizeStart)
            el.addEventListener('pointermove', onResizeMove)
            el.addEventListener('pointerup', onResizeEnd)
            el.addEventListener('pointercancel', onResizeEnd)
        }
    }, { immediate: true, flush: 'post' })

    // Re-clamp on window resize so a window that was in-bounds stays in-bounds
    function onWindowResize() {
        clamp()
        opts.onCommit?.(currentRect())
    }
    if (typeof window !== 'undefined') {
        window.addEventListener('resize', onWindowResize)
    }

    onBeforeUnmount(() => {
        stopHeaderWatch()
        stopGripWatch()
        if (typeof window !== 'undefined') window.removeEventListener('resize', onWindowResize)
    })

    clamp()

    return { x, y, width, height }
}
